// A light 3D preview of the room: isometric SVG, no WebGL. Back walls stand at full height
// with their windows and doors; front walls are drawn as a low curb so the room stays open to
// the viewer; zones are boxes with their partitions, coloured by kind.

import { cn } from "@artisan/ui";

import type { RoomDraft } from "../project/model";
import { onWall, type RoomGeometry, roomGeometry } from "./plan-view";
import { nth } from "../lib/at";

type V3 = [number, number, number];

const C = Math.cos(Math.PI / 6);
const S = Math.sin(Math.PI / 6);

function iso([x, y, z]: V3): [number, number] {
  return [(x - y) * C, (x + y) * S - z];
}

const poly = (pts: V3[]) =>
  pts
    .map(iso)
    .map(([a, b]) => `${a.toFixed(1)},${b.toFixed(1)}`)
    .join(" ");

interface Face {
  depth: number;
  pts: V3[];
  fill: string;
  stroke: string;
  strokeWidth?: number;
  dash?: string;
  key: string;
  lines?: [V3, V3][];
}

function wallFrame(g: RoomGeometry, i: number) {
  const n = g.pts.length;
  const a = nth(g.pts, i);
  const b = nth(g.pts, (i + 1) % n);
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  const u: [number, number] = [(b[0] - a[0]) / len, (b[1] - a[1]) / len];
  const inward: [number, number] = [u[1], -u[0]];
  return { a, b, len, u, inward };
}

export function IsoView({ room, className, selectedZone }: { room: RoomDraft; className?: string; selectedZone?: string | null }) {
  const g = roomGeometry(room);
  // The viewer looks from +x+y (south-east of a north-up plan): walls whose room side faces
  // that way are the back walls.
  const ceiling = (room.ceiling?.um ?? 2_700_000) / 1000;
  const n = g.pts.length;
  const faces: Face[] = [];
  const view: [number, number] = [1, 1];
  const isBack = (i: number) => {
    const f = wallFrame(g, i);
    return f.inward[0] * view[0] + f.inward[1] * view[1] > 0;
  };

  // floor
  const floor = g.pts.map(([x, y]) => [x, y, 0] as V3);

  for (let i = 0; i < n; i++) {
    const f = wallFrame(g, i);
    const back = isBack(i);
    const h = back ? ceiling : 120;
    const quad: V3[] = [
      [f.a[0], f.a[1], 0],
      [f.b[0], f.b[1], 0],
      [f.b[0], f.b[1], h],
      [f.a[0], f.a[1], h],
    ];
    const mid = (f.a[0] + f.b[0]) / 2 + (f.a[1] + f.b[1]) / 2;
    faces.push({
      depth: back ? -1e12 + mid : 1e12 + mid,
      pts: quad,
      fill: back ? "color-mix(in oklab, var(--card) 88%, var(--muted-foreground))" : "var(--card)",
      stroke: "var(--border)",
      key: `w${i}`,
    });
    if (back) {
      for (const o of room.openings) {
        if (o.wall !== i || !o.offset || !o.width || !o.height) continue;
        const s0 = o.offset.um / 1000;
        const s1 = s0 + o.width.um / 1000;
        const z0 = o.sill?.um ? o.sill.um / 1000 : 0;
        const z1 = Math.min(ceiling, z0 + o.height.um / 1000);
        const p0 = onWall(g, i, s0, 2);
        const p1 = onWall(g, i, s1, 2);
        faces.push({
          depth: -1e12 + mid + 1,
          pts: [
            [p0[0], p0[1], z0],
            [p1[0], p1[1], z0],
            [p1[0], p1[1], z1],
            [p0[0], p0[1], z1],
          ],
          fill: o.kind === "window" ? "color-mix(in oklab, var(--chart-2) 22%, var(--card))" : "color-mix(in oklab, var(--muted-foreground) 25%, var(--card))",
          stroke: o.kind === "window" ? "var(--chart-2)" : "var(--muted-foreground)",
          key: `o${o.id}`,
        });
      }
    }
  }

  for (const z of room.zones) {
    const f = wallFrame(g, z.wall);
    const o = z.offset / 1000;
    const len = Math.min(z.length / 1000, f.len - o);
    const d = z.depth / 1000;
    const z0 = z.elevation / 1000;
    const z1 = z0 + z.height / 1000;
    const P = (along: number, inward: number, h: number): V3 => {
      const p = onWall(g, z.wall, along, inward);
      return [p[0], p[1], h];
    };
    const selected = selectedZone === z.id;
    const base = z.kind === "wall" ? 30 : z.kind === "tall" ? 50 : 45;
    const shade = (pct: number) => `color-mix(in oklab, var(--oak) ${pct}%, var(--card))`;
    const stroke = selected ? "var(--primary)" : "color-mix(in oklab, var(--oak) 70%, black)";
    const centre = onWall(g, z.wall, o + len / 2, d / 2);
    const depth = centre[0] + centre[1] + (z.kind === "wall" ? 0.5 : 0);
    const front = f.inward[0] + f.inward[1] > 0; // the room-side face looks at the viewer
    const faceAt = (dd: number): V3[] => [P(o, dd, z0), P(o + len, dd, z0), P(o + len, dd, z1), P(o, dd, z1)];
    const partitionLines = (dd: number): [V3, V3][] => z.partitions.slice(1).map((p) => [P(o + p.offset / 1000, dd, z0), P(o + p.offset / 1000, dd, z1)]);
    // visible end: the end whose outward direction points at the viewer
    const startOut = -(f.u[0] + f.u[1]) > 0;
    const endAt = startOut ? o : o + len;
    faces.push({
      depth,
      key: `z${z.id}-end`,
      pts: [P(endAt, 0, z0), P(endAt, d, z0), P(endAt, d, z1), P(endAt, 0, z1)],
      fill: shade(base + 18),
      stroke,
    });
    faces.push({
      depth: depth + 0.1,
      key: `z${z.id}-face`,
      pts: faceAt(front ? d : 0),
      fill: shade(base + 30),
      stroke,
      strokeWidth: selected ? 2.5 : 1,
      lines: partitionLines(front ? d : 0),
    });
    faces.push({
      depth: depth + 0.2,
      key: `z${z.id}-top`,
      pts: [P(o, 0, z1), P(o + len, 0, z1), P(o + len, d, z1), P(o, d, z1)],
      fill: shade(base),
      stroke,
    });
  }

  faces.sort((p, q) => p.depth - q.depth);

  const all = [...floor, ...faces.flatMap((f) => f.pts)].map(iso);
  const minX = Math.min(...all.map((p) => p[0]));
  const maxX = Math.max(...all.map((p) => p[0]));
  const minY = Math.min(...all.map((p) => p[1]));
  const maxY = Math.max(...all.map((p) => p[1]));
  const pad = Math.max(maxX - minX, maxY - minY) * 0.06;
  const sw = Math.max(maxX - minX, maxY - minY) / 600;

  return (
    <svg
      viewBox={`${minX - pad} ${minY - pad} ${maxX - minX + 2 * pad} ${maxY - minY + 2 * pad}`}
      className={cn("block", className)}
      role="img"
      aria-label={`${room.name}: 3D харагдац`}
    >
      <polygon points={poly(floor)} fill="var(--muted)" stroke="var(--border)" strokeWidth={sw} />
      {faces.map((f) => (
        <g key={f.key}>
          <polygon points={poly(f.pts)} fill={f.fill} stroke={f.stroke} strokeWidth={sw * (f.strokeWidth ?? 1)} strokeLinejoin="round" />
          {f.lines?.map(([p, q], i) => {
            const [x1, y1] = iso(p);
            const [x2, y2] = iso(q);
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={f.stroke} strokeWidth={sw} />;
          })}
        </g>
      ))}
    </svg>
  );
}
